/*
 *   Copyright (c) 2021 vlinder Inc.
 *   All rights reserved.
 *   This software is proprietary of vlinder Inc. and can not be distributed / used for any purpose outside of vlinder Inc. without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useRef, useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Image,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import {
  ZoomInIcon,
  ZoomOutIcon,
  ArrowRightIcon,
  ArrowDownIcon,
} from '@heroicons/react/solid';

import {
  GraphCanvas,
  darkTheme,
  LayoutTypes,
  Theme,
  SphereWithIcon,
  useSelection,
  RadialMenu,
} from 'reagraph';
import './styles.css';
import { makeInitialNodesAndEdges } from './data.config';

export interface INodeType {
  id: string;
  label?: string;
  [key: string]: any;
}

export interface IEdgeType {
  id: string;
  source: string;
  target: string;
  label?: string;
  [key: string]: any;
}

export interface IDirectedAcyclicGraphProps {
  height?: string;
  width?: string;
  nodes: INodeType[];
  edges: IEdgeType[];
  nodeColor?: string;
  edgeColor?: string;
  arrowColor?: string;
  onNodeClickCB?(id: string): void;
}

export const DirectedAcyclicGraphWidget = ({
  height,
  width,
  nodes,
  edges,
  nodeColor,
  edgeColor,
  arrowColor,
  onNodeClickCB,
}: IDirectedAcyclicGraphProps) => {
  const ref = useRef<GraphCanvasRef | null>(null);
  const [loType, setLoType] = useState<LayoutTypes>('hierarchicalLr');
  const { formattedNodes, formattedEdges } = makeInitialNodesAndEdges(
    nodes,
    edges
  );
  const { selections, onNodeClick, onCanvasClick } = useSelection({
    ref: ref,
    nodes: formattedNodes,
    edges: formattedEdges,
    type: 'single',
  });
  useEffect(() => {
    ref.current?.centerGraph();
  }, []);
  useEffect(() => {
    setTimeout(() => {
      ref.current?.centerGraph();
    }, 1800);
  }, [loType]);
  const theme: Theme = {
    ...darkTheme,
    node: {
      ...darkTheme.node,
      activeFill: nodeColor,
      label: {
        ...darkTheme.node.label,
        activeColor: nodeColor,
        // color: '#ffffff',
      },
    },
    edge: {
      ...darkTheme.edge,
      fill: nodeColor,
      activeFill: edgeColor,
      label: {
        ...darkTheme.edge.label,
        activeColor: edgeColor,
        // color: '#ffffff',
      },
    },
    arrow: {
      ...darkTheme.arrow,
      fill: nodeColor,
      activeFill: arrowColor,
    },
  };
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        <div
          style={{
            height: height,
            width: width,
            margin: 15,
            position: 'relative',
          }}
        >
          <div
            style={{
              zIndex: 9,
              position: 'absolute',
              top: 15,
              right: 15,
              background: 'transparent',
              padding: 1,
              color: 'white',
              width: '160px',
            }}
          >
            <ButtonGroup
              className="ms-2 ms-lg-3"
              style={{ display: 'block', width: '100%' }}
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Left To Right</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => {
                    setLoType('hierarchicalLr');
                  }}
                >
                  <ArrowRightIcon className="icon icon-xs me-2" />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Top to Bottom</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => setLoType('hierarchicalTd')}
                >
                  <ArrowDownIcon className="icon icon-xs me-2" />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Directed Graph</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => {
                    setLoType('forceDirected2d');
                  }}
                >
                  <Image
                    src={require('./assets/growth.png')}
                    width={20}
                    height={60}
                    className="icon icon-xs me-2"
                  />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
            <ButtonGroup
              className="ms-2 ms-lg-3"
              style={{ display: 'block', width: '100%', marginTop: '15px' }}
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">circular</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => {
                    setLoType('circular2d');
                  }}
                >
                  <Image
                    src={require('./assets/return.png')}
                    width={20}
                    height={60}
                    className="icon icon-xs me-2"
                  />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Radial Out</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => {
                    setLoType('radialOut2d');
                  }}
                >
                  <Image
                    src={require('./assets/radial-list.png')}
                    width={20}
                    height={60}
                    className="icon icon-xs me-2"
                  />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
            <ButtonGroup
              className="ms-2 ms-lg-3"
              style={{ display: 'block', width: '100%', marginTop: '15px' }}
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Zoom In</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => ref.current?.zoomIn()}
                >
                  <ZoomInIcon className="icon icon-xs me-2" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Zoom Out</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => ref.current?.zoomOut()}
                >
                  <ZoomOutIcon className="icon icon-xs me-2" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="m-0">Fit to Screen</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={() => ref.current?.centerGraph()}
                >
                  <Image
                    src={require('./assets/fit.png')}
                    width={20}
                    height={60}
                    className="icon icon-xs me-2"
                  />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
          </div>
          <GraphCanvas
            labelType={'auto'}
            edgeLabelPosition={'above'}
            ref={ref}
            theme={theme}
            nodes={formattedNodes}
            edges={formattedEdges}
            layoutType={loType}
            onCanvasClick={onCanvasClick}
            onNodeClick={onNodeClick}
            // sizingType={'pagerank'}
            renderNode={({ node, ...rest }) => (
              <SphereWithIcon {...rest} node={node} image={node?.icon || ''} />
            )}
            contextMenu={({
              data,
              canCollapse,
              isCollapsed,
              onCollapse,
              onClose,
            }) => (
              <RadialMenu
                onClose={onClose}
                items={[
                  {
                    label: 'View',
                    onClick: () => {
                      onClose();
                      onNodeClickCB && onNodeClickCB(data.id);
                      setTimeout(() => {
                        ref.current?.centerGraph();
                      }, 800);
                    },
                  },
                  ...(canCollapse
                    ? [
                        {
                          label: isCollapsed ? 'Expand' : 'Collapse',
                          onClick: () => {
                            onCollapse &&
                              onCollapse(
                                data,
                                canCollapse,
                                isCollapsed,
                                onCollapse,
                                onClose
                              );
                            setTimeout(() => {
                              ref.current?.centerGraph();
                            }, 800);
                          },
                        },
                      ]
                    : []),
                ]}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};

DirectedAcyclicGraphWidget.defaultProps = {
  height: '70vh',
  width: '100%',
  nodeColor: '#ffca00',
  edgeColor: '#ff8a00',
  arrowColor: '#ff8a00',
};
